import React, { useState } from 'react';
import { Box, Button, TextField, FormControlLabel, Checkbox, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from "react-i18next";
import TinyCustomFallback from 'components/Custom/TinyCustomFallback';
import { useSnackbar } from 'contexts/SnackbarContextProvider';
import "../../../css/components/Calendar/Holiday/HolidayModal.css";
import "../../../css/main.css";
import { DateTime } from 'luxon';

const CreateHolidayForm = ({
    handleCreateHoliday,
    countries,
    onCloseCreateForm,
    workspaceId
}) => {
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();

    const [newHoliday, setNewHoliday] = useState({
        name: '',
        local_name: '',
        date: '',
        country: 'Custom',
        affects_all_country: 'True',
        sub_region: '',
        source: 'Custom',
        workspace: workspaceId
    });
    const [isLocalSameAsEnglish, setIsLocalSameAsEnglish] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === "local_name" && isLocalSameAsEnglish){
            setNewHoliday(prev => ({ ...prev, [name]: value, name: value }));    
            return;
        }
        setNewHoliday(prev => ({ ...prev, [name]: value }));
    };

    const handleLocalSameAsEnglishToggle = () => {
        setIsLocalSameAsEnglish(prev => !prev);
        setNewHoliday(prev => ({ ...prev, name: prev.local_name }));
        
    };

    const handleCountryChange = (e) => {
        const { value } = e.target;
        setNewHoliday(prev => ({ ...prev, country: value }));
    };

    const isValidDate = newHoliday.date && DateTime.fromISO(newHoliday.date).isValid;
    const isValidNames = newHoliday.local_name.trim() !== '' && (isLocalSameAsEnglish || newHoliday.name.trim() !== '');
    const isFormValid = isValidDate && isValidNames;

    const onSubmit = async () => {
        if (!isFormValid || !newHoliday.country) {
            showSnackbar(t("calendar.holiday_form_validation_error"), 'error');
            return;

        }
        setLoading(true);
        try {
            const holidayToCreate = { ...newHoliday };
            if (holidayToCreate.country === 'Custom') {
                holidayToCreate.country = '';
            }
            await handleCreateHoliday(holidayToCreate);
            setNewHoliday({
                name: '',
                local_name: '',
                date: '',
                country: 'Custom',
                affects_all_country: '',
                sub_region: '',
                source: '',
                workspace: workspaceId
            });
            setIsLocalSameAsEnglish(false);
        } catch (error) {
            console.error("Error creating holiday:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box mt={2} className="create-holiday-form">
            <TextField
                label={t("calendar.holiday_local")}
                name="local_name"
                value={newHoliday.local_name}
                onChange={handleInputChange}
                margin="normal"
                className="holiday-modal-textfield"
                InputLabelProps={{ className: "white-txt-color" }}
                required
            />
            <FormControlLabel
                control={
                    <Checkbox
                        checked={isLocalSameAsEnglish}
                        onChange={handleLocalSameAsEnglishToggle}
                        color="primary"
                    />
                }
                label={t("calendar.holiday_mark_local")}
                className="white-txt-color"
            />
            <TextField
                label={t("calendar.holiday_name")}
                name="name"
                value={newHoliday.name}
                onChange={handleInputChange}
                margin="normal"
                className="holiday-modal-textfield"
                InputLabelProps={{ className: "white-txt-color" }}
                disabled={isLocalSameAsEnglish}
                required={!isLocalSameAsEnglish}
            />
            <TextField
                label={t("global.date")}
                name="date"
                type="date"
                value={newHoliday.date}
                onChange={handleInputChange}
                margin="normal"
                className="holiday-modal-textfield"
                InputLabelProps={{ className: "white-txt-color", shrink: true }}
                required
            />
            <FormControl fullWidth margin="normal">
                <InputLabel id="country-select-label-create" className="white-txt-color">
                    {t("calendar.holiday_country")}
                </InputLabel>
                <Select
                    labelId="country-select-label-create"
                    name="country"
                    value={newHoliday.country || ''}
                    onChange={handleCountryChange}
                    label={t("calendar.holiday_country")}
                    className="holiday-modal-textfield"
                    MenuProps={{
                        PaperProps: {
                            className: "holiday-modal-select-menu",
                        },
                    }}
                    required
                >
                    <MenuItem value="Custom">{t("calendar.holiday_custom_country")}</MenuItem>
                    {countries
                        .filter((country) => country !== 'Custom')
                        .map((country) => (
                            <MenuItem key={country} value={country}>
                                <img
                                    key={country}
                                    src={`https://flagcdn.com/w80/${country.toLowerCase()}.png`}
                                    alt={`${country} Flag`}
                                    style={{ width: 20, height: 14, marginLeft: "4px" }}
                                />
                                {country}
                            </MenuItem>
                        ))}
                </Select>
            </FormControl>

            <Box className="holiday-modal-title-button" mt={2}>
                <Button
                    variant="outlined"
                    className="decline-button"
                    onClick={onCloseCreateForm}
                >
                    <CancelIcon style={{ marginRight: 4 }} />
                    {t("global.cancel")}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onSubmit}

                    className="accept-button"
                    disabled={loading ||  newHoliday.name == '' ||  newHoliday.name == ''
                        ||  newHoliday.local_name == '' ||  newHoliday.date == ''}
                >
                    {loading ? <TinyCustomFallback /> : (
                        <>
                            <CheckCircleIcon style={{ marginRight: 4 }} />
                            {t("calendar.holiday_create_button")}
                        </>
                    )}
                </Button>
            </Box>
        </Box>
    );
};

export default CreateHolidayForm;
